import { createPortal } from 'react-dom';
import type { CSSProperties, ReactNode } from 'react';
import { useNodeId } from '../../components';
import { DEFAULT_TOOLBAR_OFFSET, getNodeToolbarTransform, Position } from '../../core';
import type { ToolbarAlign } from '../../core';
import { selectDomNode, selectNodeById, selectSelectedNodeCount, selectViewport, useMindmapStore } from '../../store';
import { classNames } from '../../utils';

export type NodeToolbarProps = {
  /** Defaults to the node it is rendered in. */
  nodeId?: string;
  /** Default: shown while its node is the only selected node (v11). */
  isVisible?: boolean;
  position?: Position;
  /** Gap to the node in screen px. */
  offset?: number;
  align?: ToolbarAlign;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

/**
 * v11 `<NodeToolbar>`: content floating beside a node. It is drawn over the canvas, outside the zoomed viewport, so it
 * follows the node but keeps its size at every zoom. `nodrag nopan`: pressing it neither drags the node nor pans.
 */
export const NodeToolbar = ({
  nodeId,
  isVisible,
  position = Position.Top,
  offset = DEFAULT_TOOLBAR_OFFSET,
  align = 'center',
  className,
  style,
  children,
}: NodeToolbarProps) => {
  const contextNodeId = useNodeId();
  const id = nodeId ?? contextNodeId ?? '';
  const internal = useMindmapStore(selectNodeById(id));
  const viewport = useMindmapStore(selectViewport);
  const selectedCount = useMindmapStore(selectSelectedNodeCount);
  const renderer = useMindmapStore(selectDomNode);
  const shown = isVisible ?? (Boolean(internal?.node.selected) && selectedCount === 1);
  if (!shown || !internal || !renderer || internal.node.hidden) return null;

  const nodeRect = { ...internal.positionAbsolute, width: internal.width ?? 0, height: internal.height ?? 0 };
  const transform = getNodeToolbarTransform(nodeRect, viewport, position, offset, align);
  return createPortal(
    <div className={classNames('cm-node-toolbar', 'nodrag', 'nopan', className)} style={{ ...style, transform }} data-id={id}>
      {children}
    </div>,
    renderer,
  );
};
